import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Box, 
  CircularProgress, 
  IconButton,
  Typography,
  Alert,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { configureCKEditorUpload } from './ckEditorUploadAdapter';
import api from '../api/axios';

// Lazy load ClassicEditor to avoid conflicts with DecoupledEditor
let ClassicEditor = null;
const loadClassicEditor = async () => {
  if (!ClassicEditor) {
    const module = await import('@ckeditor/ckeditor5-build-classic');
    ClassicEditor = module.default;
  }
  return ClassicEditor;
};

export default function LearningContentPDFViewer({ open, onClose, pdfUrl, subtaskTitle, subtaskCode, qualificationId, savedContent }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState('');
  const [classicEditorLoaded, setClassicEditorLoaded] = useState(false);
  const [originalContent, setOriginalContent] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  // Fetch the raw content when the dialog opens
  useEffect(() => {
    if (open) {
      // If savedContent is provided, use it directly
      if (savedContent) {
        setContent(savedContent);
        setOriginalContent(savedContent);
        setHasChanges(false);
        setLoading(false);
        setError(null);
      } else if (subtaskCode && qualificationId) {
        fetchContent();
      }
    }
  }, [open, subtaskCode, qualificationId, savedContent]);
  
  const fetchContent = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch the learning content for this subtask
      const response = await api.get(`/qualifications/${qualificationId}/learning-content`);
      const contentData = response.data?.content || [];
      
      const item = contentData.find(item => item.subtask_code === subtaskCode);
      if (item) {
        // Try to get content_html first, then fall back to raw_text
        const textContent = item.content_html || item.raw_text || item.generated_content?.content_html || '';
        setContent(textContent);
        setOriginalContent(textContent);
        setHasChanges(false);
      } else {
        setError('No content found for this task');
        setContent('');
        setOriginalContent('');
      }
    } catch (err) {

      setError('Failed to load content: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };
  
  
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      // Update the content via API
      const response = await api.put(`/qualifications/${qualificationId}/learning-content/${subtaskCode}`, {
        content_html: content,
        raw_text: content
      });
      
      if (response.data?.success) {
        setOriginalContent(content);
        setHasChanges(false);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (err) {

      setError('Failed to save content: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };
  
  const handleClose = () => {
    if (hasChanges) {
      const shouldClose = window.confirm('You have unsaved changes. Are you sure you want to close?');
      if (!shouldClose) return;
    }
    
    setContent('');
    setOriginalContent('');
    setHasChanges(false);
    setSaveSuccess(false);
    setError(null);
    onClose();
  };
  
  return (
    <>
      <style>
        {`
          .ck-editor__editable {
            min-height: 400px !important;
            max-height: 60vh !important;
            overflow-y: auto !important;
          }
          .ck-editor {
            height: 100% !important;
          }
          .ck.ck-editor__main > .ck-editor__editable {
            border: 1px solid #c4c4c4 !important;
            border-radius: 4px !important;
          }
        `}
      </style>
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">Edit Content: {subtaskTitle}</Typography>
          {hasChanges && <Chip label="Unsaved Changes" color="warning" size="small" />}
          {saveSuccess && <Chip label="Saved" color="success" size="small" />}
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%'
          }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading content...</Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <Box className="learner-reading-host" sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              {classicEditorLoaded && ClassicEditor && (
                <CKEditor
                  editor={ClassicEditor}
              data={content}
              onChange={(event, editor) => {
                const data = editor.getData();
                setContent(data);
                setHasChanges(data !== originalContent);
                setSaveSuccess(false);
              }}
              config={{
                toolbar: [
                  'heading', '|',
                  'bold', 'italic', 'underline', 'strikethrough', '|',
                  'bulletedList', 'numberedList', '|',
                  'outdent', 'indent', '|',
                  'blockQuote', 'insertTable', '|',
                  'link', '|',
                  'undo', 'redo'
                ],
                table: {
                  contentToolbar: [
                    'tableColumn',
                    'tableRow',
                    'mergeTableCells'
                  ]
                }
              }}
              onReady={(editor) => {
                configureCKEditorUpload(editor);
                // Set editor height to fill available space
                editor.editing.view.change((writer) => {
                  writer.setStyle('height', '100%', editor.editing.view.document.getRoot());
                });
              }}
            />
              )}
              {!classicEditorLoaded && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                  <Typography sx={{ ml: 2 }}>Loading editor...</Typography>
                </Box>
              )}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={handleSave} 
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />} 
          color="primary"
          disabled={!hasChanges || saving || loading}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
        <Button onClick={handleClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}



